import { type AirplaneInit, hooks } from "@/store.js";
import NiceModal, { useModal } from "@ebay/nice-modal-react";
import showErrMessage from "@/utils/showErrMessage.js";
import {
	Autocomplete,
	AutocompleteItem,
	Button,
	Checkbox,
	Form,
	Input,
	Popover,
	PopoverContent,
	PopoverTrigger,
	Select,
	SelectItem,
	Table,
	TableBody,
	TableCell,
	TableColumn,
	TableHeader,
	TableRow,
} from "@heroui/react";
import { useRequest, useUpdateEffect } from "ahooks";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import isEqual from "react-fast-compare";
import { toast } from "sonner";
import { Drawer } from "vaul";
import { ModalProvider } from "react-modal-hook";

import { planDetailEvent } from "@/pages/PlanDetail/utils/event.js";

import SheetWrapper, {
	SheetWrapperHeader,
} from "@/components/SheetWrapperV2/index.jsx";
import { Sheet } from "react-modal-sheet";
import AirPlane from "../../AirPlane.jsx";
import VerdantProvider from "@/components/VerdantProvider.jsx";
import useAirplane from "@/pages/PlanDetail/hooks/useAirplane.js";

interface IProps {
	defaultValue?: AirplaneInit;
	onClose: () => void;
	date: string;
	// onSuccess: () => void;
	planId: string;
	no: string;
	id: string;
}

const INPUT_CLASSNAMES = {
	input: "font-semibold placeholder:text-gray-300 text-xl",
	inputWrapper: " pb-2",
	label: "top-3 opacity-60 text-xs",
};

const INPUT_CLASSNAMES_SM = {
	trigger: "px-0 justify-center ",
	value: " text-gray-400",
	label: " opacity-50 top-3",
	input: "placeholder:text-gray-400 font-semibold placeholder:font-normal",
	selectorButton: "text-gray-300",
};

const Inner = memo(({ defaultValue, date, planId, no, id }: IProps) => {
	const [counter, setCounter] = useState(defaultValue?.counter);
	const [gate, setGate] = useState(defaultValue?.gate);
	const [platform, setPlatform] = useState(defaultValue?.platform);
	const [pnr, setPnr] = useState(defaultValue?.pnr);
	const [bagDirect, setBagDirect] = useState(defaultValue?.bagDirect);
	const [orderId, setOrderId] = useState(defaultValue?.orderId);
	const [checkedBagKg, setCheckedBagKg] = useState(defaultValue?.checkedBagKg);
	const [seats, setSeats] = useState(() =>
		defaultValue?.seats?.length
			? defaultValue.seats
			: [
					["Y", "", ""],
					["S", "", ""],
				],
	);
	const airplaneInfo = useAirplane(no) || {};

	const client = hooks.useClient();
	const { runAsync, loading } = useRequest(
		async () => {
			if (!planId) throw new Error("参数不全");

			const postObj = {
				planId,
				counter,
				gate,
				bagDirect,
				seats,
				platform,
				pnr,
				orderId,
				checkedBagKg,
			};

			if (!id) {
				if (!no) throw new Error("参数不全");
				return await client.airplane.put({
					...postObj,
					no,
				});
			}

			const res = await client.airplane.get(id).resolved;
			res?.update(postObj);
		},
		{
			manual: true,
			onError: showErrMessage,
			onSuccess() {
				toast.success("修改成功");
				setHasEdit(false);
			},
		},
	);

	const [hasEdit, setHasEdit] = useState(false);
	useUpdateEffect(() => {
		if (hasEdit) return;
		setHasEdit(true);
	}, [counter, gate, bagDirect, seats, platform, pnr, orderId, checkedBagKg]);

	const [portalContainer, setPortalContainer] = useState<HTMLDivElement | null>(
		null,
	);

	const [memberActive, setMemberActive] = useState(0);
	const sameLineOccupy = useMemo(
		() =>
			seats.filter(
				(item, index) =>
					index !== memberActive && item[1] === seats[memberActive]?.[1],
			),
		[memberActive, seats],
	);

	return (
		<SheetWrapper
			onContentRef={setPortalContainer}
			overlay={
				<div className="  left-0 p-4 h-[300px] flex flex-col  justify-center  ">
					<AirPlane
						no={no}
						date={date}
						planId={planId}
						id={id}
						previewValue={{ counter, gate, seats, checkedBagKg }}
					/>
				</div>
			}
		>
			<Form
				className="w-full !block pb-safe -mt-3"
				onSubmit={async (e) => {
					e.stopPropagation();
					e.preventDefault();

					runAsync();
				}}
			>
				{/* <AddFormHeader onClose={onClose} loading={loading} /> */}

				<SheetWrapperHeader
					title="航班详情"
					align="left"
					okText="保存"
					hideOk={!hasEdit}
				/>

				<div
					className="px-4 pt-4 pb-safe overflow-scroll"
					style={{ height: "calc(100vh - 380px)" }}
				>
					<div className="grid grid-cols-2 gap-4">
						<Input
							size="lg"
							placeholder="A,B"
							variant="bordered"
							label="值机台"
							defaultValue={counter}
							onValueChange={setCounter}
							startContent={
								<i className="ic-outline md-how_to_reg  text-gray-500 text-lg" />
							}
							classNames={INPUT_CLASSNAMES}
						/>

						<Input
							size="lg"
							placeholder="01"
							variant="bordered"
							label="登机口"
							defaultValue={gate}
							onValueChange={setGate}
							startContent={
								<i className="ic-outline md-door_sliding  text-gray-500 text-lg" />
							}
							classNames={INPUT_CLASSNAMES}
						/>
					</div>

					<Checkbox
						// @ts-ignore type
						isSelected={bagDirect}
						onValueChange={setBagDirect}
						className="mt-3 mb-2"
					>
						行李直达
					</Checkbox>

					<section className="bg-white rounded-2xl my-2 border border-gray-200 mb-4">
						<header className="bg-indigo-50/50 rounded-t-2xl pb-2 grid grid-cols-2">
							{[{ label: "向妤" }, { label: "陈式坚" }].map((item, index) => {
								return (
									<span
										className={`${memberActive === index ? "bg-white font-semibold text-gray-900" : "text-black/40"}  rounded-t-2xl   block text-center py-2`}
										key={item.label}
										onClick={() => {
											setMemberActive(index);
										}}
									>
										{item.label}
									</span>
								);
							})}
						</header>
						<div
							className={`bg-white rounded-b-3xl  -mt-2 pb-4 ${memberActive === 0 ? " rounded-tr-2xl" : " rounded-tl-2xl"}`}
						>
							<div className="flex   px-4 pt-4">
								{/* @ts-ignore type */}
								<Select
									variant="bordered"
									popoverProps={{
										portalContainer: portalContainer as Element,
										className: "w-20",
									}}
									label="座位"
									labelPlacement="outside"
									size="sm"
									placeholder="01"
									className="w-12 shrink-0 mr-5   "
									// biome-ignore lint/complexity/noUselessFragments: <explanation>
									selectorIcon={<></>}
									classNames={{
										...INPUT_CLASSNAMES_SM,
										trigger: `${INPUT_CLASSNAMES_SM.trigger} mt-1`,
										value: `${INPUT_CLASSNAMES_SM.value} text-center font-semibold `,
									}}
									selectedKeys={[seats[memberActive]?.[1]]}
									onChange={(e: { target: { value: string } }) => {
										setSeats((prev) => [
											...prev.slice(0, memberActive),
											[
												...prev[memberActive].slice(0, 1),
												e.target.value,
												...prev[memberActive].slice(-1),
											],
											...prev.slice(memberActive + 1),
										]);
									}}
								>
									{Array.from({ length: 80 }, (_, i) => ({
										key: i + 1,
										label: (i + 1).toString().padStart(2, "0"),
									})).map((animal) => (
										<SelectItem key={animal.key}>{animal.label}</SelectItem>
									))}
								</Select>

								<Table
									removeWrapper
									classNames={{
										td: "p-0",
										th: "h-6 bg-white px-0  text-center min-w-4 text-gray-400 font-normal",
									}}
								>
									<TableHeader>
										{[
											"A",
											"B",
											"C",
											"",
											...(airplaneInfo.model === "波音737-800"
												? ["J", "K", "L"]
												: ["D", "E", "F"]),
										].map((i) => (
											<TableColumn key={i}>{i}</TableColumn>
										))}
									</TableHeader>
									<TableBody>
										<TableRow key="1">
											{[
												"A",
												"B",
												"C",
												"",
												...(airplaneInfo.model === "波音737-800"
													? ["J", "K", "L"]
													: ["D", "E", "F"]),
											].map((item) => {
												const isActive = seats[memberActive]?.[2] === item;
												const occupy = sameLineOccupy.find(
													(n) => n[2] === item,
												);
												return (
													<TableCell key={item}>
														{item && (
															<span
																onClick={() => {
																	if (occupy) return;
																	setSeats((prev) => [
																		...prev.slice(0, memberActive),
																		[...prev[memberActive].slice(0, 2), item],
																		...prev.slice(memberActive + 1),
																	]);
																}}
																className={`${isActive ? "border-black bg-black text-white  ic-round md-event_seat" : `${occupy ? "bg-gray-50 text-gray-500 font-semibold" : "border-gray-200 text-gray-500  ic-outline md-event_seat"}`} block ml-2  w-7 h-7 rounded-md leading-7 text-center  border  `}
															>
																{occupy?.[0]}
															</span>
														)}
													</TableCell>
												);
											})}
										</TableRow>
									</TableBody>
								</Table>
							</div>

							<div className="border-t border-dashed border-gray-200 mx-4 mt-5 mb-10" />
							<div className="px-4 ">
								{/* @ts-ignore type */}
								<Select
									placeholder="23KG"
									variant="bordered"
									label="行李额度"
									// label={<span className=" opacity-50 pb-0.5 block">平台</span>}
									size="sm"
									labelPlacement="outside"
									// @ts-ignore type
									defaultSelectedKeys={[checkedBagKg]}
									onChange={(e: { target: { value: string } }) =>
										setCheckedBagKg(e.target.value)
									}
									startContent={
										<i className="ic-outline md-luggage  text-gray-500 text-lg" />
									}
									popoverProps={{
										portalContainer: portalContainer as Element,
										// className: "w-20",
									}}
									classNames={INPUT_CLASSNAMES_SM}
								>
									{["20KG", "23KG", "2x23KG"].map((animal) => (
										<SelectItem key={animal}>{animal}</SelectItem>
									))}
								</Select>
							</div>

							<div className="grid grid-cols-2 gap-4 mx-4 pt-5 ">
								{/* @ts-ignore type */}
								<Select
									placeholder="官网"
									variant="bordered"
									label="平台"
									// label={<span className=" opacity-50 pb-0.5 block">平台</span>}
									size="sm"
									labelPlacement="outside"
									// @ts-ignore type
									defaultValue={platform}
									onChange={(e) => {
										setPlatform(e.target.value);
									}}
									startContent={
										<i className="ic-outline md-store  text-gray-500 text-lg" />
									}
									popoverProps={{
										portalContainer: portalContainer as Element,
									}}
									classNames={INPUT_CLASSNAMES_SM}
								>
									{["去哪儿", "携程", "Trip", "同程", "飞猪", "官网"].map(
										(animal) => (
											<SelectItem key={animal}>{animal}</SelectItem>
										),
									)}
								</Select>

								<Input
									size="sm"
									labelPlacement="outside"
									placeholder="ABC123"
									variant="bordered"
									label="PNR"
									defaultValue={pnr}
									onValueChange={setPnr}
									startContent={
										<i className="ic-outline md-book  text-gray-500 text-lg" />
									}
									classNames={INPUT_CLASSNAMES_SM}
								/>
							</div>

							<div className="px-4 mt-10">
								<Input
									size="sm"
									labelPlacement="outside"
									placeholder="12345678910"
									variant="bordered"
									label="订单号"
									defaultValue={orderId}
									onValueChange={setOrderId}
									startContent={
										<i className="ic-outline md-book  text-gray-500 text-lg" />
									}
									classNames={INPUT_CLASSNAMES_SM}
								/>
							</div>
						</div>
					</section>
				</div>
			</Form>
		</SheetWrapper>
	);
}, isEqual);

const Modal = NiceModal.create((props: IProps) => {
	return (
		<VerdantProvider>
			<ModalProvider>
				<Inner
					{...props}
					// onClose={() => setCloseKey(+new Date())}
				/>
			</ModalProvider>
		</VerdantProvider>
	);
});

export const UseAirplaneDetail = () => {
	const modal = useModal(Modal);
	return {
		show: modal.show,
	};
};
